// Filtro de período do Dashboard com toggle de MODO:
//   • Intradiário — calendário de UM dia (Popover + Calendar single),
//     faixa de horas "De/Até" (buckets de hora cheia, from < to) e
//     botão "Hoje".
//   • Histórico — presets 7/30/90 dias + range custom (Calendar
//     mode="range", sem futuro). A janela vive em ?startDate&?endDate.
// Tudo vive na URL — dashboard compartilhável. Dias futuros são
// desabilitados em ambos os modos (o Dashboard olha o realizado).
import { format, parse } from "date-fns"
import { enUS, es, ptBR } from "date-fns/locale"
import {
  CalendarClockIcon,
  CalendarIcon,
  CalendarRangeIcon,
  ClockIcon,
} from "lucide-react"
import { useState } from "react"
import type { DateRange } from "react-day-picker"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { ComboBox } from "@/components/ui/combo-box"
import { Calendar } from "@/components/ui/default-calendar"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useNotificationCenterDashboardSearchParams } from "@/hooks"
import {
  getNotificationDashboardDefaultDate,
  NOTIFICATION_DASHBOARD_HOURS,
  NOTIFICATION_DASHBOARD_PRESETS,
} from "@/hooks/use-notification-center-dashboard-search-params"
import { cn } from "@/lib/utils"
import { DateHelper } from "@/utils"

// Locale do date-fns por idioma do i18n (rótulo do calendário).
const DATE_FNS_LOCALES = {
  "pt-BR": ptBR,
  en: enUS,
  es,
} as const

const YYYY_MM_DD = "yyyy-MM-dd"

// "yyyy-MM-dd" → Date ao meio-dia (evita drift de timezone).
const parseDay = (value: string): Date =>
  parse(
    `${value}T12:00:00`,
    "yyyy-MM-dd'T'HH:mm:ss",
    new Date()
  )

export const DashboardPeriodFilter = () => {
  const { t, i18n } = useTranslation()
  const {
    mode,
    date,
    fromHour,
    toHour,
    startDate,
    endDate,
    activePreset,
    setDate,
    setHourRange,
    resetToToday,
    setDateRange,
    setHistoricalPreset,
    switchMode,
  } = useNotificationCenterDashboardSearchParams()

  const modePrefix =
    "pages.notificationCenter.dashboardPage.mode"

  const calendarLocale =
    DATE_FNS_LOCALES[
      i18n.language as keyof typeof DATE_FNS_LOCALES
    ] ?? ptBR

  const modeToggle = (
    <div
      role="group"
      aria-label={t(`${modePrefix}.label`)}
      className="inline-flex rounded-md border p-0.5"
    >
      <Button
        type="button"
        size="sm"
        variant={
          mode === "intraday" ? "secondary" : "ghost"
        }
        aria-pressed={mode === "intraday"}
        onClick={() => switchMode("intraday")}
        className="gap-1.5"
      >
        <ClockIcon className="size-4" />
        {t(`${modePrefix}.intraday`)}
      </Button>
      <Button
        type="button"
        size="sm"
        variant={
          mode === "historical" ? "secondary" : "ghost"
        }
        aria-pressed={mode === "historical"}
        onClick={() => switchMode("historical")}
        className="gap-1.5"
      >
        <CalendarRangeIcon className="size-4" />
        {t(`${modePrefix}.historical`)}
      </Button>
    </div>
  )

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border p-3">
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t(`${modePrefix}.label`)}
        </Label>
        {modeToggle}
      </div>
      {mode === "intraday" ? (
        <IntradayControls
          date={date}
          fromHour={fromHour}
          toHour={toHour}
          calendarLocale={calendarLocale}
          onSelectDate={setDate}
          onSelectHourRange={setHourRange}
          onResetToday={resetToToday}
        />
      ) : (
        <HistoricalControls
          startDate={startDate}
          endDate={endDate}
          activePreset={activePreset}
          calendarLocale={calendarLocale}
          onSelectPreset={setHistoricalPreset}
          onSelectRange={setDateRange}
        />
      )}
    </div>
  )
}

// ── Intradiário ─────────────────────────────────────────────────────
interface IntradayControlsProps {
  date: string
  fromHour?: string
  toHour?: string
  calendarLocale: (typeof DATE_FNS_LOCALES)[keyof typeof DATE_FNS_LOCALES]
  onSelectDate: (date: string) => void
  onSelectHourRange: (patch: {
    fromHour?: string
    toHour?: string
  }) => void
  onResetToday: () => void
}

const IntradayControls = ({
  date,
  fromHour,
  toHour,
  calendarLocale,
  onSelectDate,
  onSelectHourRange,
  onResetToday,
}: IntradayControlsProps) => {
  const { t } = useTranslation()
  const periodPrefix =
    "pages.notificationCenter.dashboardPage.period"

  const selectedDate = parseDay(date)
  const isToday =
    date === getNotificationDashboardDefaultDate()

  // Pontas efetivas exibidas nos selects quando não há recorte na
  // URL (dia cheio) — e usadas para desabilitar opções inválidas.
  const effectiveFrom =
    fromHour ?? NOTIFICATION_DASHBOARD_HOURS[0]
  const effectiveTo =
    toHour ??
    NOTIFICATION_DASHBOARD_HOURS[
      NOTIFICATION_DASHBOARD_HOURS.length - 1
    ]

  // from < to: cada select só oferece horas que mantêm a faixa
  // válida em relação à outra ponta.
  const fromHourOptions =
    NOTIFICATION_DASHBOARD_HOURS.filter(
      (hour) => hour < effectiveTo
    ).map((hour) => ({ label: hour, value: hour }))
  const toHourOptions = NOTIFICATION_DASHBOARD_HOURS.filter(
    (hour) => hour > effectiveFrom
  ).map((hour) => ({ label: hour, value: hour }))

  return (
    <>
      <div className="flex min-w-52 flex-col gap-1">
        <Label
          id="dashboard-day-label"
          className="text-xs text-muted-foreground"
        >
          {t(`${periodPrefix}.dayLabel`)}
        </Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-labelledby="dashboard-day-label"
              className="justify-between text-left font-normal capitalize"
            >
              {DateHelper.formatDate(
                selectedDate,
                "dd/MM/yyyy"
              )}
              <CalendarIcon className="size-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
          >
            <Calendar
              initialFocus
              mode="single"
              defaultMonth={selectedDate}
              selected={selectedDate}
              className="capitalize"
              locale={calendarLocale}
              // Sem dias futuros — o Dashboard olha o realizado.
              disabled={{ after: new Date() }}
              onSelect={(day) => {
                if (!day) return
                onSelectDate(
                  DateHelper.formatDate(day, YYYY_MM_DD)
                )
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex w-28 flex-col gap-1">
        <Label
          id="dashboard-from-hour-label"
          className="text-xs text-muted-foreground"
        >
          {t(`${periodPrefix}.fromHour`)}
        </Label>
        <ComboBox
          items={fromHourOptions}
          selected={effectiveFrom}
          onSelect={(value) =>
            onSelectHourRange({ fromHour: value })
          }
          placeholder={NOTIFICATION_DASHBOARD_HOURS[0]}
          align="start"
        />
      </div>
      <div className="flex w-28 flex-col gap-1">
        <Label
          id="dashboard-to-hour-label"
          className="text-xs text-muted-foreground"
        >
          {t(`${periodPrefix}.toHour`)}
        </Label>
        <ComboBox
          items={toHourOptions}
          selected={effectiveTo}
          onSelect={(value) =>
            onSelectHourRange({ toHour: value })
          }
          placeholder={
            NOTIFICATION_DASHBOARD_HOURS[
              NOTIFICATION_DASHBOARD_HOURS.length - 1
            ]
          }
          align="start"
        />
      </div>
      <Button
        type="button"
        variant={isToday ? "secondary" : "outline"}
        onClick={onResetToday}
        className="gap-1.5"
      >
        <CalendarClockIcon className="size-4" />
        {t(`${periodPrefix}.todayButton`)}
      </Button>
    </>
  )
}

// ── Histórico ───────────────────────────────────────────────────────
interface HistoricalControlsProps {
  startDate?: string
  endDate?: string
  activePreset: number | null
  calendarLocale: (typeof DATE_FNS_LOCALES)[keyof typeof DATE_FNS_LOCALES]
  onSelectPreset: (days: number) => void
  onSelectRange: (range: {
    startDate: string
    endDate: string
  }) => void
}

const HistoricalControls = ({
  startDate,
  endDate,
  activePreset,
  calendarLocale,
  onSelectPreset,
  onSelectRange,
}: HistoricalControlsProps) => {
  const { t } = useTranslation()
  const periodPrefix =
    "pages.notificationCenter.dashboardPage.period"

  const [isOpen, setIsOpen] = useState(false)
  const selectedRange: DateRange | undefined =
    startDate && endDate
      ? { from: parseDay(startDate), to: parseDay(endDate) }
      : undefined
  const [tempRange, setTempRange] = useState<
    DateRange | undefined
  >(selectedRange)

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    // Ao abrir, o rascunho parte da janela vigente.
    if (open) setTempRange(selectedRange)
  }

  const handleApply = () => {
    if (!tempRange?.from || !tempRange?.to) return
    onSelectRange({
      startDate: format(tempRange.from, YYYY_MM_DD),
      endDate: format(tempRange.to, YYYY_MM_DD),
    })
    setIsOpen(false)
  }

  const rangeLabel =
    startDate && endDate
      ? `${DateHelper.formatDate(parseDay(startDate), "dd/MM/yyyy")} – ${DateHelper.formatDate(parseDay(endDate), "dd/MM/yyyy")}`
      : t(`${periodPrefix}.selectPeriod`)

  return (
    <>
      <div className="flex flex-col gap-1">
        <Label className="text-xs text-muted-foreground">
          {t(`${periodPrefix}.presetsLabel`)}
        </Label>
        <div className="inline-flex rounded-md border p-0.5">
          {NOTIFICATION_DASHBOARD_PRESETS.map((days) => (
            <Button
              key={days}
              type="button"
              size="sm"
              variant={
                activePreset === days
                  ? "secondary"
                  : "ghost"
              }
              aria-pressed={activePreset === days}
              onClick={() => onSelectPreset(days)}
            >
              {t(`${periodPrefix}.presetDays`, {
                count: days,
              })}
            </Button>
          ))}
        </div>
      </div>
      <div className="flex min-w-56 flex-col gap-1">
        <Label
          id="dashboard-range-label"
          className="text-xs text-muted-foreground"
        >
          {t(`${periodPrefix}.rangeLabel`)}
        </Label>
        <Popover
          open={isOpen}
          onOpenChange={handleOpenChange}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-labelledby="dashboard-range-label"
              className={cn(
                "justify-between text-left font-normal capitalize",
                !selectedRange && "text-muted-foreground"
              )}
            >
              {rangeLabel}
              <CalendarRangeIcon className="size-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            align="start"
          >
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={
                tempRange?.from ?? selectedRange?.from
              }
              selected={tempRange}
              onSelect={setTempRange}
              numberOfMonths={2}
              className="capitalize"
              locale={calendarLocale}
              // Sem dias futuros — o Dashboard olha o realizado.
              disabled={{ after: new Date() }}
            />
            <div className="flex items-center justify-end gap-2 border-t p-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                {t(`${periodPrefix}.cancel`)}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleApply}
                disabled={
                  !tempRange?.from || !tempRange?.to
                }
              >
                {t(`${periodPrefix}.apply`)}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </>
  )
}
